import { CheckoutService } from '../services/CheckoutService';
import { ExpressCheckoutBIService } from '../services/ExpressCheckoutBIService';
import type { ExpressCheckoutSettings } from '../../../types';
import { LineItemModel } from '../models/LineItem.model';
import { FastFlowRedirectionReason } from './consts';
import { cartV2 } from '@wix/ecom';

export async function shouldNavigateToFastFlowCheckout({
  lineItems,
  expressCheckoutSettings,
  checkoutService,
  isMemberLoggedIn,
  biService,
  checkoutId,
  purchaseFlowId,
  deliveryOptionsErrors,
  shouldUpdateShippingAddress,
}: {
  lineItems: LineItemModel[];
  checkoutId: string;
  purchaseFlowId: string;
  expressCheckoutSettings?: ExpressCheckoutSettings;
  checkoutService: CheckoutService;
  biService: ExpressCheckoutBIService;
  isMemberLoggedIn: boolean;
  deliveryOptionsErrors?: {
    hasShippingZipCodeError?: boolean;
    hasShippingSubdivisionError?: boolean;
  };
  shouldUpdateShippingAddress: boolean;
}) {
  const reasonsToNavigateToFastFlow = await getReasonsToNavigateToFastFlow({
    lineItems,
    expressCheckoutSettings,
    checkoutService,
    isMemberLoggedIn,
    deliveryOptionsErrors,
    shouldUpdateShippingAddress,
  });

  const shouldNavigateToFastFlow = reasonsToNavigateToFastFlow.length > 0;

  if (shouldNavigateToFastFlow) {
    biService.reportNavigateToFastFlowCheckout({
      checkoutId,
      purchaseFlowId,
      reason: reasonsToNavigateToFastFlow.join(),
    });
  }

  return shouldNavigateToFastFlow;
}

export async function getReasonsToNavigateToFastFlow({
  lineItems,
  expressCheckoutSettings,
  checkoutService,
  isMemberLoggedIn,
  deliveryOptionsErrors,
  shouldUpdateShippingAddress,
}: {
  lineItems: LineItemModel[];
  expressCheckoutSettings?: ExpressCheckoutSettings;
  checkoutService: CheckoutService;
  isMemberLoggedIn: boolean;
  deliveryOptionsErrors?: {
    hasShippingZipCodeError?: boolean;
    hasShippingSubdivisionError?: boolean;
  };
  shouldUpdateShippingAddress: boolean;
}) {
  const getHasCustomField = async () => {
    const [hasRequiredDataExtendedFields, hasRequiredAdditionalInfoField] =
      await Promise.all([
        checkoutService.doesFormHaveRequiredDataExtendedFields(),
        checkoutService.doesFormHaveRequiredAdditionalInfoField(),
      ]);

    return hasRequiredDataExtendedFields || hasRequiredAdditionalInfoField;
  };

  const policiesExist = Boolean(expressCheckoutSettings?.hasPolicies);
  const policyAgreementRequired = Boolean(
    expressCheckoutSettings?.requirePolicyAgreement,
  );
  const hasRequiredPolicies = policiesExist && policyAgreementRequired;
  const hasLineItemPolicies = Boolean(
    lineItems?.some((lineItem) => lineItem.hasPolicies) &&
      policyAgreementRequired,
  );
  const hasCustomField = await getHasCustomField();

  const hasMemberOnlyCheckout = Boolean(
    !isMemberLoggedIn && lineItems?.some((lineItem) => lineItem.membersOnly),
  );
  const hasShippableItem = Boolean(
    lineItems?.some((lineItem) => lineItem.shippable),
  );

  return [
    ...(hasLineItemPolicies
      ? [FastFlowRedirectionReason.hasLineItemPolicies]
      : []),
    ...(hasCustomField ? [FastFlowRedirectionReason.hasCustomField] : []),
    ...(hasMemberOnlyCheckout
      ? [FastFlowRedirectionReason.hasMemberOnlyCheckout]
      : []),
    ...(hasRequiredPolicies
      ? [FastFlowRedirectionReason.hasRequiredPolicies]
      : []),
    ...(hasShippableItem &&
    shouldUpdateShippingAddress &&
    deliveryOptionsErrors?.hasShippingZipCodeError
      ? [FastFlowRedirectionReason.hasShippingZipCodeError]
      : []),
    ...(hasShippableItem &&
    shouldUpdateShippingAddress &&
    deliveryOptionsErrors?.hasShippingSubdivisionError
      ? [FastFlowRedirectionReason.hasShippingSubdivisionError]
      : []),
  ];
}

export function shouldNavigateToFastFlowCheckoutAfterPlaceOrderError({
  error,
  checkoutId,
  purchaseFlowId,
  biService,
}: {
  error: unknown;
  checkoutId: string;
  purchaseFlowId: string;
  biService: ExpressCheckoutBIService;
}): boolean {
  const applicationError = (
    error as { details?: { applicationError?: { code?: string; data?: any } } }
  )?.details?.applicationError;
  const code = applicationError?.code;

  const reason = code
    ? getPlaceOrderFastFlowReason(code, applicationError?.data)
    : undefined;

  if (reason) {
    biService.reportNavigateToFastFlowCheckout({
      checkoutId,
      purchaseFlowId,
      reason,
    });
  }

  return Boolean(reason);
}

export const PLACE_ORDER_ERROR_CODE_TO_FAST_FLOW_REASON: Record<
  string,
  string
> = {
  VIOLATIONS_WITH_ERROR_SEVERITY: FastFlowRedirectionReason.hasViolations,
  CART_PRICES_OUT_OF_SYNC:
    FastFlowRedirectionReason.hasTotalAmountMismatchWithLatestSeen,
  MISSING_DELIVERY_METHOD: FastFlowRedirectionReason.shouldChooseShippingMethod,
  DELIVERY_METHOD_NOT_APPLIED_TO_ALL_LINE_ITEMS:
    FastFlowRedirectionReason.shouldChooseShippingMethod,
  DELIVERY_METHOD_NO_LONGER_AVAILABLE:
    FastFlowRedirectionReason.shouldChooseShippingMethod,
};

const DELIVERY_METHOD_FAST_FLOW_VIOLATION_CODES: cartV2.ViolationCode[] = [
  cartV2.ViolationCode.MISSING_DELIVERY_METHOD,
  cartV2.ViolationCode.DELIVERY_METHOD_NOT_APPLIED_TO_ALL_LINE_ITEMS,
  cartV2.ViolationCode.DELIVERY_METHOD_NO_LONGER_AVAILABLE,
];

function getPlaceOrderFastFlowReason(
  code: string,
  data: any,
): string | undefined {
  if (PLACE_ORDER_ERROR_CODE_TO_FAST_FLOW_REASON[code]) {
    return PLACE_ORDER_ERROR_CODE_TO_FAST_FLOW_REASON[code];
  }

  if (code === 'INVALID_CART') {
    return getInvalidCartFastFlowReason(data);
  }

  return undefined;
}

function getInvalidCartFastFlowReason(data?: {
  violations?: cartV2.V2Violation[];
}): string | undefined {
  const violations = data?.violations;
  if (!violations) {
    return undefined;
  }

  const reasons: FastFlowRedirectionReason[] = [];

  if (
    violations.some(
      (v) =>
        v.scope === cartV2.ViolationScope.TAX &&
        v.code === cartV2.ViolationCode.INVALID_POSTAL_CODE,
    )
  ) {
    reasons.push(FastFlowRedirectionReason.hasTaxZipCodeError);
  }

  if (
    violations.some(
      (v) =>
        v.scope === cartV2.ViolationScope.TAX &&
        v.code === cartV2.ViolationCode.INVALID_SUBDIVISION,
    )
  ) {
    reasons.push(FastFlowRedirectionReason.hasTaxSubdivisionError);
  }

  if (
    violations.some((v) =>
      DELIVERY_METHOD_FAST_FLOW_VIOLATION_CODES.some((code) => v.code === code),
    )
  ) {
    reasons.push(FastFlowRedirectionReason.shouldChooseShippingMethod);
  }

  if (
    violations.some(
      (v) =>
        v.code === cartV2.ViolationCode.CUSTOM_VALIDATION &&
        v.severity === cartV2.ViolationSeverity.ERROR,
    )
  ) {
    reasons.push(FastFlowRedirectionReason.hasViolations);
  }

  return reasons.length > 0 ? reasons.join() : undefined;
}
